import { RastreioCancerPerguntaOpcaoEnum } from 'graphql/types.generated'
import { isEmpty } from 'util/checks'
import { afterBirthdate, beforeEqualTo, createValidator, minLength, msg, required } from 'util/validation'

import { AnoSelectModel } from '../citopatologico/AnoSelectField'
import { cirurgiaAnoFields } from './anamnese/model-anamnese'
import { MamografiaPerguntaField, RequisicaoExameMamografiaFormValues } from './model-mamografia'

const MSG_ANO_POSTERIOR_NASCIMENTO = 'Deve ser posterior ao ano de nascimento do cidadão.'
const MSG_ANO_ANTERIOR_IGUAL_ATUAL = 'Deve ser anterior ou igual ao ano atual.'
const MSG_CIRURGIA_ANO = 'É necessário preencher o ano em que foi realizada a cirurgia.'

const anoNumber = (value: unknown): number | undefined => {
  if (isEmpty(value)) return undefined
  const nome = typeof value === 'object' ? (value as AnoSelectModel).nome : String(value)
  const ano = Number(nome)
  return Number.isNaN(ano) ? undefined : ano
}

const anoAfterBirthYear = (value: unknown, dataNascimento: string): string | undefined => {
  const ano = anoNumber(value)
  if (ano === undefined) return undefined
  const birthYear = new Date(dataNascimento).getFullYear()
  return ano < birthYear ? MSG_ANO_POSTERIOR_NASCIMENTO : undefined
}

const anoBeforeOrEqualCurrentYear = (value: unknown, today: Date): string | undefined => {
  const ano = anoNumber(value)
  if (ano === undefined) return undefined
  return ano > today.getFullYear() ? MSG_ANO_ANTERIOR_IGUAL_ATUAL : undefined
}

const dataSolicitacaoError = (value: string, dataNascimento: string, today: Date): string | undefined => {
  if (!value) return undefined
  if (afterBirthdate(value, dataNascimento)) return msg('afterBirthdate', null)
  if (beforeEqualTo(value, today.toDateString(), 'data atual')) return msg('beforeToday', null)
  return undefined
}

export const mamografiaAnamneseValidator = (dataNascimento: string, serverTime: Date) =>
  createValidator<RequisicaoExameMamografiaFormValues>(
    {
      [MamografiaPerguntaField.noduloCaroco]: [required],
      [MamografiaPerguntaField.riscoCancerMama]: [required],
      [MamografiaPerguntaField.mamasExaminadas]: [required],
      [MamografiaPerguntaField.fezMamografia]: [required],
      [MamografiaPerguntaField.fezRadioterapia]: [required],
    },
    (values, errors) => {
      if (!values) return errors

      const anoUltimaMamografia = values[MamografiaPerguntaField.anoUltimaMamografia]
      errors[MamografiaPerguntaField.anoUltimaMamografia] =
        (values[MamografiaPerguntaField.fezMamografia] === RastreioCancerPerguntaOpcaoEnum.SIM
          ? required(anoUltimaMamografia)
          : undefined) ??
        anoAfterBirthYear(anoUltimaMamografia, dataNascimento) ??
        anoBeforeOrEqualCurrentYear(anoUltimaMamografia, serverTime)

      if (values[MamografiaPerguntaField.fezRadioterapia] === RastreioCancerPerguntaOpcaoEnum.SIM) {
        errors[MamografiaPerguntaField.radioterapiaLocalizacao] = required(
          values[MamografiaPerguntaField.radioterapiaLocalizacao]
        )
      }

      const localizacao = values[MamografiaPerguntaField.radioterapiaLocalizacao]
      const habilitaDireita =
        localizacao === RastreioCancerPerguntaOpcaoEnum.DIREITA || localizacao === RastreioCancerPerguntaOpcaoEnum.AMBAS
      const habilitaEsquerda =
        localizacao === RastreioCancerPerguntaOpcaoEnum.ESQUERDA ||
        localizacao === RastreioCancerPerguntaOpcaoEnum.AMBAS

      const anoRadioterapiaDireita = values[MamografiaPerguntaField.anoRadioterapiaMamaDireita]
      errors[MamografiaPerguntaField.anoRadioterapiaMamaDireita] =
        (habilitaDireita ? required(anoRadioterapiaDireita) : undefined) ??
        anoAfterBirthYear(anoRadioterapiaDireita, dataNascimento) ??
        anoBeforeOrEqualCurrentYear(anoRadioterapiaDireita, serverTime)

      const anoRadioterapiaEsquerda = values[MamografiaPerguntaField.anoRadioterapiaMamaEsquerda]
      errors[MamografiaPerguntaField.anoRadioterapiaMamaEsquerda] =
        (habilitaEsquerda ? required(anoRadioterapiaEsquerda) : undefined) ??
        anoAfterBirthYear(anoRadioterapiaEsquerda, dataNascimento) ??
        anoBeforeOrEqualCurrentYear(anoRadioterapiaEsquerda, serverTime)

      if (values[MamografiaPerguntaField.fezCirurgiaMama]) {
        const algumAnoPreenchido = cirurgiaAnoFields.some((field) => !isEmpty(values[field]))
        if (!algumAnoPreenchido) {
          errors[MamografiaPerguntaField.fezCirurgiaMama] = MSG_CIRURGIA_ANO
        }
      }

      return errors
    }
  )

export const mamografiaIndicacaoClinicaValidator = createValidator<RequisicaoExameMamografiaFormValues>(
  {
    [MamografiaPerguntaField.tipoMamografia]: [required],
  },
  (values, errors) => {
    if (!values) return errors
    if (values[MamografiaPerguntaField.tipoMamografia] === RastreioCancerPerguntaOpcaoEnum.RASTREAMENTO) {
      errors[MamografiaPerguntaField.mamografiaRastreamento] = required(
        values[MamografiaPerguntaField.mamografiaRastreamento]
      )
    }
    return errors
  }
)

export const mamografiaDadosExameValidator = (dataNascimento: string, today: Date) =>
  createValidator<RequisicaoExameMamografiaFormValues>(
    {
      [MamografiaPerguntaField.dataSolicitacao]: [required],
      [MamografiaPerguntaField.nomeResponsavel]: [required],
      [MamografiaPerguntaField.cns]: [minLength(15), required],
      [MamografiaPerguntaField.siglaNomeConselho]: [required],
      [MamografiaPerguntaField.numeroConselho]: [required],
    },
    (values, errors) => {
      if (!values) return errors
      const dataSolicitacao = values[MamografiaPerguntaField.dataSolicitacao] as string
      if (dataSolicitacao) {
        errors[MamografiaPerguntaField.dataSolicitacao] = dataSolicitacaoError(dataSolicitacao, dataNascimento, today)
      }
      return errors
    }
  )
